import React from 'react';
import { useQuery } from 'react-query';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';
import { analyticsAPI } from '../services/api';
import { Card, StatCard, Spinner, PageHeader } from '../components/common';
import Layout from '../components/layout/Layout';

const CULORI = ['#2d2d7e','#1d9e75','#d85a30','#e24b4a','#ba7517','#534ab7'];

export default function AnalyticsPage() {
  const { data: ov,   isLoading: l1 } = useQuery('analytics-overview',    () => analyticsAPI.overview().then(r => r.data.data));
  const { data: dept, isLoading: l2 } = useQuery('analytics-departamente', () => analyticsAPI.departamente().then(r => r.data.data));

  if (l1 || l2) return <Layout><Spinner message="Se încarcă statisticile..." /></Layout>;

  const trend = (ov?.trend7zile || []).map(r => ({
    zi:    r.zi?.slice(5),  // MM-DD
    cereri: parseInt(r.numar)
  }));

  const peStatus = (ov?.peStatus || []).map(r => ({
    name:  STATUS_LABELS[r.status] || r.status,
    value: parseInt(r.numar)
  }));

  const peTip = (ov?.peTip || []).map(r => ({
    name:  TIP_LABELS[r.tip] || r.tip,
    numar: parseInt(r.numar)
  }));

  return (
    <Layout>
      <PageHeader title="Statistici" subtitle="Performanța primăriei în timp real" />

      {/* KPIs principale */}
      <div style={grid4}>
        <StatCard label="Total cereri"      value={ov?.total || 0}       color="#2d2d7e" />
        <StatCard label="Timp mediu (zile)" value={ov?.timpMediu || 0}   color="#854f0b" sub="pentru rezolvare" />
        <StatCard label="SLA depășit"       value={ov?.slaDepasit || 0}  color="#a32d2d" sub="cereri întârziate" />
        <StatCard label="Tipuri sesizări"   value={ov?.peTip?.length || 0} color="#1d9e75" />
      </div>

      <div style={grid2}>
        {/* Trend 7 zile */}
        <Card title="Cereri înregistrate — ultimele 7 zile">
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="zi"    tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="cereri" stroke="#2d2d7e" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* Distribuție pe status */}
        <Card title="Distribuție pe status">
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={peStatus} cx="50%" cy="50%" outerRadius={75}
                dataKey="value" label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`}
                labelLine={false} fontSize={11}
              >
                {peStatus.map((_, i) => <Cell key={i} fill={CULORI[i % CULORI.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Distribuție pe tip */}
      <Card title="Cereri pe tip">
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={peTip} layout="vertical" margin={{ left: 80 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={80} />
            <Tooltip />
            <Bar dataKey="numar" fill="#2d2d7e" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Tabel departamente */}
      <Card title="Performanță pe departamente">
        <div style={tableWrap}>
          <div style={thRow}>
            {['Departament','Total','Rezolvate','SLA depășit','Timp mediu (zile)'].map(h => (
              <span key={h} style={th}>{h}</span>
            ))}
          </div>
          {(dept || []).map(d => (
            <div key={d.cod} style={tdRow}>
              <span style={{ ...td, fontWeight: '600' }}>{d.nume}</span>
              <span style={td}>{d.total_cereri}</span>
              <span style={{ ...td, color: '#3b6d11' }}>{d.rezolvate}</span>
              <span style={{ ...td, color: d.sla_depasit > 0 ? '#a32d2d' : '#3b6d11' }}>{d.sla_depasit}</span>
              <span style={td}>{d.zile_medii || '—'}</span>
            </div>
          ))}
        </div>
      </Card>
    </Layout>
  );
}

const STATUS_LABELS = { inregistrata:'Înregistrată', in_analiza:'În analiză', in_lucru:'În lucru', rezolvata:'Rezolvată', respinsa:'Respinsă', arhivata:'Arhivată' };
const TIP_LABELS    = { sesizare:'Sesizare', solicitare:'Solicitare', petitie:'Petiție', reclamatie:'Reclamație', informatie:'Inf. publică' };

const grid4 = { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '16px', marginBottom: '20px' };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' };
const tableWrap = { overflowX: 'auto' };
const thRow = { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', marginBottom: '4px' };
const tdRow = { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', padding: '12px', borderBottom: '1px solid #f5f5f5' };
const th = { fontSize: '12px', fontWeight: '600', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' };
const td = { fontSize: '14px', color: '#333' };
